/**
 * Step 3 · Confirm details + consent (ux-flows §2 step 3). Pre-filled from what the candidate
 * entered; the referee confirms/edits ONLY what appears in the letter — their name, how they knew
 * the candidate, and where. Consent lives here (recording, transcription, AI drafting) as a calm
 * notice, not a legal wall: continuing IS the consent, captured server-side against the referee
 * attestation. On submit we confirm details, record consent, and advance to the record step.
 *
 * `roleThen` from the mock has no backend column (referee_contacts carries name / relationship /
 * company only, WP-B11), so it is intentionally not a field here.
 */
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { ChoiceChips, type ChoiceOption } from '@/components/ui/choice-chips';
import { TextField } from '@/components/ui/field';
import { apiClient } from '@/lib/api-client';
import { RefereeApiError } from '@/lib/api-types';
import { useAdvance } from '@/session/useAdvance';
import { useSession } from '@/session/useSession';
import { useSessionStore } from '@/session/store';

type ConfirmRelationship = 'manager' | 'colleague' | 'client' | 'mentor';

const RELATIONSHIP_OPTIONS: ChoiceOption<ConfirmRelationship>[] = [
  { value: 'manager', label: 'Manager' },
  { value: 'colleague', label: 'Colleague' },
  { value: 'client', label: 'Client' },
  { value: 'mentor', label: 'Mentor' },
];
const RELATIONSHIP_VALUES = RELATIONSHIP_OPTIONS.map((o) => o.value);

const confirmSchema = z.object({
  fullName: z.string().trim().min(1, 'Please enter your name.').max(120, 'That name is too long.'),
  company: z.string().trim().max(120, 'That is too long.').optional(),
  relationship: z.enum(['manager', 'colleague', 'client', 'mentor']).optional(),
});
type ConfirmValues = z.infer<typeof confirmSchema>;

export function Confirm() {
  const { token, context } = useSession();
  const { advance, pending, error } = useAdvance();
  const setConsentAcknowledged = useSessionStore((s) => s.setConsentAcknowledged);

  const referee = context?.referee;
  const firstName = context?.candidate.name.split(' ')[0] ?? 'them';
  /** A 409 from either verb behind this form: the request is past the point of confirming. */
  const alreadyDone = error instanceof RefereeApiError && error.kind === 'conflict';
  const prefillRelationship =
    referee?.relationship && (RELATIONSHIP_VALUES as string[]).includes(referee.relationship)
      ? (referee.relationship as ConfirmRelationship)
      : undefined;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ConfirmValues>({
    resolver: zodResolver(confirmSchema),
    defaultValues: {
      fullName: referee?.name ?? '',
      company: referee?.company ?? '',
      relationship: prefillRelationship,
    },
  });

  const onSubmit = (values: ConfirmValues) =>
    advance(async () => {
      const ctx = await apiClient.confirmDetails(token, {
        name: values.fullName,
        company: values.company?.trim() ? values.company.trim() : undefined,
        relationship: values.relationship,
      });
      // Continuing IS the consent (ux-flows §2 step 3): record it server-side against the CURRENT
      // attestation the context surfaced (the backend rejects a stale version), then remember the
      // ack for this visit so the record step (WP-F5) can proceed without re-prompting.
      const consent =
        context?.consentDocumentKey && context.consentDocumentVersion
          ? { documentKey: context.consentDocumentKey, version: context.consentDocumentVersion }
          : undefined;
      await apiClient.recordConsent(token, consent);
      setConsentAcknowledged(true);
      return ctx;
    }, 'record');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col" noValidate>
      <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-balance">
        Let&rsquo;s get your details right.
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        Only what appears in the letter — nothing else.
      </p>

      <TextField
        label="Your name"
        autoComplete="name"
        icon={<UserIcon />}
        error={errors.fullName?.message}
        {...register('fullName')}
      />

      <TextField
        label="Where you worked together"
        autoComplete="organization"
        icon={<BuildingIcon />}
        error={errors.company?.message}
        {...register('company')}
      />

      <fieldset className="mt-3">
        <legend className="mb-1.5 block text-[12px] font-medium text-ink-quiet">
          How you knew {firstName}
        </legend>
        <Controller
          control={control}
          name="relationship"
          render={({ field }) => (
            <ChoiceChips
              legend={`How you knew ${firstName}`}
              options={RELATIONSHIP_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              allowDeselect
            />
          )}
        />
      </fieldset>

      {/* Consent notice — a calm assurance, not a legal wall. Continuing records it. */}
      <div className="mt-5 flex items-start gap-2.5 rounded-md bg-sage-tint p-3.5 text-[11.5px] font-medium leading-relaxed text-sage-text">
        <span className="flex-none pt-0.5">
          <ShieldCheckIcon />
        </span>
        <p>
          We record your voice, transcribe it, and draft the letter from only your words. Nothing is
          sent until you approve and sign. We only use your details for this reference — never for
          anything else.
        </p>
      </div>

      <div className="mt-auto flex flex-col gap-2.5 pt-6">
        <Button type="submit" variant="coral" size="block" disabled={pending || !context}>
          Continue
        </Button>
        <p className="text-center text-[11px] leading-relaxed text-ink-quiet">
          By continuing you agree we can record and transcribe your voice to draft the letter.{' '}
          <a
            href="https://bymyword.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-coral-text underline-offset-2 hover:underline"
          >
            How your recording is handled
          </a>
        </p>
        {alreadyDone ? (
          // A 409 here is not a fault, it is a fact: BOTH verbs behind this form are
          // `Accepted`-only server-side (`confirm-details` answers `conflict`, `consent` answers
          // `cannot_consent`), so any request that has moved past `accepted` — most plainly a
          // `rerecord_requested` one, whose details and consent were settled before its first take
          // — refuses this submit forever. Retrying cannot help, so this must not say "try again";
          // it says what is true and hands them the way on. `/r/:token` re-resolves the status and
          // the step router lands them wherever they actually belong.
          <div role="alert" className="flex flex-col items-center gap-2.5">
            <p className="text-center text-[12px] font-medium leading-relaxed text-coral-text">
              You&rsquo;ve already done this part — nothing here needs changing.
            </p>
            <Button asChild variant="ghost" size="block">
              <Link to={`/r/${token}`}>Carry on</Link>
            </Button>
          </div>
        ) : (
          !!error && (
            <p role="alert" className="text-center text-[12px] font-medium text-coral-text">
              Something went wrong — please try again.
            </p>
          )
        )}
      </div>
    </form>
  );
}

/* --- inline icons -------------------------------------------------------------------------- */

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" aria-hidden="true">
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth={1.8} />
      <path d="M4 20c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" aria-hidden="true">
      <path d="M4 21V5a1.5 1.5 0 011.5-1.5h8A1.5 1.5 0 0115 5v16" stroke="currentColor" strokeWidth={1.8} />
      <path
        d="M15 9h3.5A1.5 1.5 0 0120 10.5V21M4 21h16M8 8h3M8 12h3M8 16h3"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
      />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
      <path d="M12 2l7 4v6c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
